<section class="bg-white shadow-sm hover:shadow-md transition rounded-xl border border-gray-100">
  <!-- Card Header -->
  <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
    <div class="flex items-center gap-3">
      <h2 class="text-base font-semibold text-gray-900 flex items-center gap-2">
        Settlement Wallets
        <span
          *ngIf="wallets.length > 0"
          class="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"
        >
          {{ wallets.length }}
        </span>
      </h2>
      <div class="relative group cursor-help">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <div
          class="absolute left-0 top-full mt-2 hidden group-hover:block w-72 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-30 pointer-events-none"
        >
          Dedicated Solana deposit wallets for SEPA (via Noah) and direct Crypto deposits. Use "Flush Funds" to sweep stuck funds to the user's primary Privy wallet.
        </div>
      </div>
    </div>

    <!-- Refresh Button -->
    <button
      (click)="fetchWallets()"
      [disabled]="loading"
      title="Refresh balances"
      class="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="w-4 h-4"
        [ngClass]="{ 'animate-spin': loading }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
    </button>
  </div>

  <!-- Loading Skeleton -->
  <div *ngIf="loading && wallets.length === 0" class="p-5 space-y-4">
    <div class="animate-pulse flex items-center justify-between py-3 border-b border-gray-100">
      <div class="space-y-2">
        <div class="h-4 bg-gray-200 rounded w-24"></div>
        <div class="h-3 bg-gray-100 rounded w-48"></div>
      </div>
      <div class="h-8 bg-gray-200 rounded w-28"></div>
    </div>
    <div class="animate-pulse flex items-center justify-between py-3">
      <div class="space-y-2">
        <div class="h-4 bg-gray-200 rounded w-24"></div>
        <div class="h-3 bg-gray-100 rounded w-48"></div>
      </div>
      <div class="h-8 bg-gray-200 rounded w-28"></div>
    </div>
  </div>

  <!-- Error Alert -->
  <div *ngIf="error" class="p-5">
    <div class="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between text-xs text-red-700">
      <span>{{ error }}</span>
      <button (click)="fetchWallets()" class="font-medium underline hover:text-red-900 ml-3">
        Retry
      </button>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!loading && !error && wallets.length === 0" class="p-6 text-center text-sm text-gray-500">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="w-10 h-10 mx-auto text-gray-300 mb-2"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="1.5"
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
    <p class="font-medium text-gray-700">No settlement wallets found</p>
    <p class="text-xs text-gray-400 mt-0.5">This user does not have any Solana settlement deposit wallets assigned yet.</p>
  </div>

  <!-- Wallets Table / List -->
  <div *ngIf="wallets.length > 0" class="overflow-x-auto rounded-b-xl">
    <table class="min-w-full divide-y divide-gray-100">
      <thead class="bg-gray-50/75">
        <tr>
          <th scope="col" class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Type
          </th>
          <th scope="col" class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Deposit Address
          </th>
          <th scope="col" class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Live Balances
          </th>
          <th scope="col" class="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Created
          </th>
          <th scope="col" class="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Action
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100 bg-white">
        <tr *ngFor="let wallet of wallets" class="hover:bg-gray-50/60 transition">
          <!-- Type Badge -->
          <td class="px-5 py-4 whitespace-nowrap">
            <span
              class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
              [ngClass]="{
                'bg-blue-50 text-blue-700 border border-blue-200': wallet.wallet_type === 'SEPA',
                'bg-purple-50 text-purple-700 border border-purple-200': wallet.wallet_type === 'CRYPTO'
              }"
            >
              {{ wallet.wallet_type }}
            </span>
          </td>

          <!-- Address Column -->
          <td class="px-5 py-4 whitespace-nowrap">
            <div class="flex items-center gap-2">
              <a
                [href]="'https://solscan.io/account/' + wallet.address"
                target="_blank"
                rel="noopener noreferrer"
                class="font-mono text-xs text-indigo-600 hover:text-indigo-900 transition flex items-center gap-1 group"
                title="View on Solscan"
              >
                <span>{{ wallet.address.slice(0, 6) }}...{{ wallet.address.slice(-6) }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-3.5 h-3.5 text-gray-400 group-hover:text-indigo-600 transition"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </a>

              <!-- Copy Button -->
              <button
                (click)="copyAddress(wallet.address)"
                type="button"
                class="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100 transition"
                [title]="copiedAddress === wallet.address ? 'Copied!' : 'Copy full address'"
              >
                <ng-container *ngIf="copiedAddress === wallet.address; else copyIcon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3.5 h-3.5 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                </ng-container>
                <ng-template #copyIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </ng-template>
              </button>
            </div>
          </td>

          <!-- Live Balances Column -->
          <td class="px-5 py-4 whitespace-nowrap">
            <div class="flex items-center gap-1.5 flex-wrap">
              <!-- USDC -->
              <span
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors"
                [ngClass]="getUsdc(wallet) > 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-gray-50 border-gray-200/80 text-gray-500'"
              >
                <span
                  class="text-[10px] font-bold uppercase tracking-wider"
                  [ngClass]="getUsdc(wallet) > 0 ? 'text-emerald-600' : 'text-gray-400'"
                >USDC</span>
                <span
                  class="font-semibold"
                  [ngClass]="getUsdc(wallet) > 0 ? 'text-emerald-700 font-bold' : 'text-gray-700'"
                >
                  ${{ getUsdc(wallet).toFixed(2) }}
                </span>
              </span>

              <!-- EURC -->
              <span
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors"
                [ngClass]="getEurc(wallet) > 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-gray-50 border-gray-200/80 text-gray-500'"
              >
                <span
                  class="text-[10px] font-bold uppercase tracking-wider"
                  [ngClass]="getEurc(wallet) > 0 ? 'text-emerald-600' : 'text-gray-400'"
                >EURC</span>
                <span
                  class="font-semibold"
                  [ngClass]="getEurc(wallet) > 0 ? 'text-emerald-700 font-bold' : 'text-gray-700'"
                >
                  €{{ getEurc(wallet).toFixed(2) }}
                </span>
              </span>

              <!-- USDT -->
              <span
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium border transition-colors"
                [ngClass]="getUsdt(wallet) > 0
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                  : 'bg-gray-50 border-gray-200/80 text-gray-500'"
              >
                <span
                  class="text-[10px] font-bold uppercase tracking-wider"
                  [ngClass]="getUsdt(wallet) > 0 ? 'text-emerald-600' : 'text-gray-400'"
                >USDT</span>
                <span
                  class="font-semibold"
                  [ngClass]="getUsdt(wallet) > 0 ? 'text-emerald-700 font-bold' : 'text-gray-700'"
                >
                  ${{ getUsdt(wallet).toFixed(2) }}
                </span>
              </span>
            </div>
          </td>

          <!-- Created At Column -->
          <td class="px-5 py-4 whitespace-nowrap text-xs text-gray-500">
            {{ wallet.created_at | date:'medium' }}
          </td>

          <!-- Action Column -->
          <td class="px-5 py-4 whitespace-nowrap text-right">
            <!-- Cooldown State -->
            <ng-container *ngIf="isCoolingDown(wallet.uuid)">
              <div class="relative inline-flex items-center group">
                <button
                  disabled
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 cursor-not-allowed opacity-80"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3.5 h-3.5 text-amber-500 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Settling ({{ getCooldownRemaining(wallet.uuid) }})
                </button>
                <!-- Tooltip -->
                <div
                  class="absolute right-0 bottom-full mb-2 hidden group-hover:block w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-30 text-left pointer-events-none"
                >
                  Please wait and refresh the page after 5 minutes in order for funds to move and settle.
                </div>
              </div>
            </ng-container>

            <!-- Normal Action States (not cooling down) -->
            <ng-container *ngIf="!isCoolingDown(wallet.uuid)">
              <!-- Missing Privy Wallet Disabled State -->
              <div *ngIf="!privyWalletAddress" class="relative inline-flex items-center group">
                <button
                  disabled
                  class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
                >
                  Flush Funds
                </button>
                <div
                  class="absolute right-0 bottom-full mb-2 hidden group-hover:block w-56 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-30 text-left pointer-events-none"
                >
                  User does not have an active Privy wallet to receive funds.
                </div>
              </div>

              <!-- Zero / Insufficient Flushable Balance Disabled State -->
              <ng-container *ngIf="privyWalletAddress && !hasFlushableBalance(wallet)">
                <button
                  disabled
                  title="No funds available to flush"
                  class="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 bg-gray-50 border border-gray-200 cursor-not-allowed"
                >
                  Flush Funds
                </button>
              </ng-container>

              <!-- Ready to Flush Button -->
              <ng-container *ngIf="privyWalletAddress && hasFlushableBalance(wallet)">
                <button
                  *ngIf="canFlush"
                  (click)="openFlushModal(wallet)"
                  class="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition active:scale-95"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-3.5 h-3.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                  Flush Funds
                </button>
              </ng-container>
            </ng-container>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</section>

<!-- Flush Funds Modal -->
<div
  *ngIf="isFlushModalOpen"
  class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 backdrop-blur-sm p-4"
>
  <div class="bg-white rounded-2xl p-6 w-full max-w-lg shadow-2xl border border-gray-200 relative animate-fadeIn">
    <!-- Header -->
    <div class="flex items-center justify-between pb-4 border-b border-gray-100">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">Flush Settlement Funds</h3>
        <p class="text-xs text-gray-500 mt-0.5">
          Transfer {{ flushCurrency }} from settlement wallet to user's main Privy wallet
        </p>
      </div>
      <button
        (click)="closeFlushModal()"
        [disabled]="isFlushing"
        class="text-gray-400 hover:text-gray-600 rounded-lg p-1 hover:bg-gray-100 transition disabled:opacity-50"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Success State -->
    <div *ngIf="flushSuccess" class="my-5 space-y-4">
      <div class="p-4 bg-green-50 border border-green-200 rounded-xl">
        <div class="flex items-start gap-3">
          <div class="p-1 bg-green-100 rounded-full text-green-700 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div class="flex-1 text-sm text-green-800">
            <p class="font-semibold text-base">{{ flushSuccess.message || 'Funds flushed successfully!' }}</p>
            <p *ngIf="feesCollected > 0" class="mt-1">
              Collected <span class="font-bold">{{ feesCollected }} {{ flushCurrency }}</span> fee,
              flushed <span class="font-bold">{{ flushedAmount }} {{ flushCurrency }}</span>
            </p>
            <p *ngIf="feesCollected <= 0" class="mt-1">
              Flushed: <span class="font-bold">{{ flushedAmount }} {{ flushCurrency }}</span>
            </p>
            <div class="mt-2 text-xs">
              <span class="text-green-700 font-medium">Solana Signature: </span>
              <a
                [href]="'https://solscan.io/tx/' + flushSuccess.transaction_hash"
                target="_blank"
                rel="noopener noreferrer"
                class="font-mono text-indigo-700 hover:underline inline-flex items-center gap-1 font-semibold"
              >
                {{ flushSuccess.transaction_hash.slice(0, 10) }}...{{ flushSuccess.transaction_hash.slice(-8) }}
                <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Settlement Wait Notice -->
      <div class="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-start gap-2.5">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <span class="font-semibold">Notice:</span> Please wait and refresh the page after 5 minutes in order for funds to move and settle. A 5-minute cooldown is active.
        </div>
      </div>

      <div class="flex justify-end pt-2">
        <button
          (click)="closeFlushModal()"
          class="px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition"
        >
          Done
        </button>
      </div>
    </div>

    <!-- Active Form (when not success) -->
    <div *ngIf="!flushSuccess" class="my-4 space-y-4">
      <!-- Error Message -->
      <div
        *ngIf="flushError"
        class="p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 flex items-start gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div class="flex-1">{{ flushError }}</div>
      </div>

      <!-- Transaction Route Details -->
      <div class="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs space-y-2.5">
        <div class="flex items-center justify-between">
          <span class="text-gray-500 font-medium">Source Settlement Wallet:</span>
          <span class="font-mono text-gray-800 bg-white px-2 py-0.5 rounded border border-gray-200">
            {{ selectedWallet?.address?.slice(0, 8) }}...{{ selectedWallet?.address?.slice(-6) }}
            ({{ selectedWallet?.wallet_type }})
          </span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-gray-500 font-medium">Destination Privy Wallet:</span>
          <span class="font-mono text-gray-800 bg-white px-2 py-0.5 rounded border border-gray-200">
            {{ privyWalletAddress ? privyWalletAddress.slice(0, 8) + '...' + privyWalletAddress.slice(-6) : 'None' }}
          </span>
        </div>
        <div class="flex items-center justify-between pt-1 border-t border-gray-200">
          <span class="text-gray-500 font-medium">Available Balance ({{ flushCurrency }}):</span>
          <span class="font-bold text-gray-900 text-sm">
            {{ availableFlushBalance.toFixed(2) }} {{ flushCurrency }}
          </span>
        </div>
        <!-- Other Balances in Wallet -->
        <div class="flex items-center justify-between pt-1 text-xs text-gray-500">
          <span class="text-gray-400">Other Balances:</span>
          <div class="flex items-center gap-2 font-mono text-gray-600">
            <span *ngIf="flushCurrency !== 'USDC'">USDC: ${{ getUsdc(selectedWallet).toFixed(2) }}</span>
            <span *ngIf="flushCurrency !== 'EURC'">EURC: €{{ getEurc(selectedWallet).toFixed(2) }}</span>
            <span *ngIf="flushCurrency !== 'USDT'">USDT: ${{ getUsdt(selectedWallet).toFixed(2) }}</span>
          </div>
        </div>
      </div>

      <!-- Currency Selector (only when more than one currency holds funds) -->
      <div *ngIf="selectedWalletFlushableCurrencies.length > 1">
        <label class="block text-xs font-semibold text-gray-700 mb-1.5">Currency to Flush</label>
        <div class="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          <button
            *ngFor="let curr of selectedWalletFlushableCurrencies"
            type="button"
            (click)="selectFlushCurrency(curr)"
            [disabled]="isFlushing"
            [ngClass]="curr === flushCurrency
              ? 'bg-white text-indigo-700 shadow-sm border border-gray-200'
              : 'text-gray-500 hover:text-gray-700'"
            class="px-3 py-1 rounded-md text-xs font-medium transition disabled:opacity-50"
          >
            {{ curr }}
          </button>
        </div>
      </div>

      <!-- Amount Input -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 mb-1.5">
          Amount to Flush ({{ flushCurrency }})
        </label>
        <div class="relative rounded-lg shadow-sm">
          <input
            type="number"
            step="0.01"
            min="0.01"
            [max]="availableFlushBalance"
            [(ngModel)]="flushAmount"
            readonly
            class="block w-full rounded-lg border-gray-300 bg-gray-50 text-gray-700 cursor-not-allowed pr-20 text-sm focus:border-gray-300 focus:ring-0 py-2.5 px-3 border"
            placeholder="0.00"
          />
          <div class="absolute inset-y-0 right-0 flex items-center pr-2">
            <span class="text-xs text-gray-400 font-medium ml-1 mr-1">{{ flushCurrency }}</span>
          </div>
        </div>

        <!-- Validation message -->
        <p *ngIf="selectedWallet && flushAmount > availableFlushBalance" class="text-xs text-red-600 mt-1">
          Amount cannot exceed the available balance of {{ availableFlushBalance.toFixed(2) }} {{ flushCurrency }}.
        </p>
        <p *ngIf="flushAmount < 0.01 && flushAmount !== 0" class="text-xs text-red-600 mt-1">
          Amount must be at least 0.01 {{ flushCurrency }}.
        </p>
      </div>

      <!-- Transaction (optional, enables fee collection) -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 mb-1.5">
          Transaction UUID <span class="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          [(ngModel)]="transactionUuid"
          [disabled]="isFlushing"
          class="block w-full rounded-lg border-gray-300 text-sm font-mono focus:border-indigo-500 focus:ring-indigo-500 py-2.5 px-3 border"
          placeholder="b3f1c2d4-..."
        />
        <p *ngIf="!isTransactionUuidValid" class="text-xs text-red-600 mt-1">
          Enter a valid transaction UUID, or leave it empty.
        </p>
        <p *ngIf="isTransactionUuidValid" class="text-xs text-gray-500 mt-1">
          If set, any fee still owed on this transaction is collected first, and the amount above is the total taken out of the wallet, fee included.
        </p>
        <p *ngIf="transactionUuid.trim() && flushCurrency !== 'USDC'" class="text-xs text-amber-600 mt-1">
          Fees can only be collected in USDC. The flush will be rejected if this transaction still owes a fee.
        </p>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
        <button
          type="button"
          (click)="closeFlushModal()"
          [disabled]="isFlushing"
          class="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          (click)="submitFlush()"
          [disabled]="!isAmountValid || !isTransactionUuidValid || isFlushing"
          class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 shadow-sm transition"
        >
          <ng-container *ngIf="!isFlushing">
            Confirm & Flush Funds
          </ng-container>
          <ng-container *ngIf="isFlushing">
            <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Executing on Solana...
          </ng-container>
        </button>
      </div>
    </div>
  </div>
</div>
